import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { ScenarioRunProto } from "build-ts/schedule";
import { fetchChatSession } from "services/AgentChatService";
import { ChatMessageType } from "build-ts/common";
import { ChatResponse } from "build-ts/chat_session";

const markdownComponents = {
  ul: ({ node, ...props }: any) => (
    <ul style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol style={{ paddingLeft: "1.25rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  li: ({ node, ...props }: any) => (
    <li style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }} {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: "0.5rem" }} {...props} />
  ),
  h1: ({ node, ...props }: any) => (
    <h1 style={{ fontSize: "1.15rem", fontWeight: "bold", margin: "1rem 0" }} {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2 style={{ fontSize: "1.05rem", fontWeight: "bold", margin: "0.875rem 0" }} {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 style={{ fontSize: "0.95rem", fontWeight: "bold", margin: "0.75rem 0" }} {...props} />
  ),
  h4: ({ node, ...props }: any) => (
    <h4 style={{ fontSize: "0.85rem", fontWeight: "bold", margin: "0.625rem 0" }} {...props} />
  ),
};

interface AnalysisScenarioInsightsCardProps {
  scenarioRun?: ScenarioRunProto;
  expandAllOutputs?: boolean;
}

// Cache chat responses to avoid duplicate API calls
const chatResponseCache = new Map<string, ChatResponse | null>();

const AnalysisScenarioInsightsCard: React.FC<AnalysisScenarioInsightsCardProps> = ({
  scenarioRun,
  expandAllOutputs = false,
}) => {
  const [chatResponse, setChatResponse] = useState<ChatResponse | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const sessionId = scenarioRun?.analyst_chat_session_id;

  useEffect(() => {
    if (!expandAllOutputs || !sessionId) return;

    if (chatResponseCache.has(sessionId)) {
      setChatResponse(chatResponseCache.get(sessionId));
      return;
    }

    setIsLoading(true);
    fetchChatSession(sessionId, false)
      .then((response) => {
        const value = response ?? null;
        chatResponseCache.set(sessionId, value);
        setChatResponse(value);
      })
      .catch(() => {
        chatResponseCache.set(sessionId, null);
        setChatResponse(null);
      })
      .finally(() => setIsLoading(false));
  }, [expandAllOutputs, sessionId]);

  const lastAiContent = chatResponse
    ? [...(chatResponse.messages || [])]
      .reverse()
      .find((m) => m.message_type === ChatMessageType.RESPONSE)
      ?.content
    : undefined;

  if (!sessionId || !expandAllOutputs) {
    return null;
  }

  return (
    <VStack spacing={2} align="stretch" ml={0} mr={0} mb={2}>
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
        px={3}
        py={1}
      >
        {isLoading ? (
          <Text fontSize="xs" color="gray.500" mb={0}>Loading...</Text>
        ) : lastAiContent ? (
          <Text fontSize="xs" color="gray.700" lineHeight="1.6" mb={0} textAlign="left">
            <ReactMarkdown components={markdownComponents}>
              {lastAiContent}
            </ReactMarkdown>
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.400" mb={0}>No output found.</Text>
        )}
      </Box>
    </VStack>
  );
};

export default AnalysisScenarioInsightsCard;
